import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import api from '@/services/api';

interface IndividualTarget {
  id: string;
  agentName: string;
  targetAmount: number;
  collectedAmount: number;
  progress?: number;
}

interface Target {
  id: string;
  type: 'company' | 'individual';
  label: string;
  month: string;
  targetAmount: number;
  collectedAmount: number;
  progress?: number;
  bankId?: string;
  bankName?: string;
  branchId?: string;
  branchName?: string;
  agentProfileId?: string;
  agentName?: string;
  parentId?: string;
  individualTargets?: IndividualTarget[];
}

interface TargetsState {
  items: Target[];
  loading: boolean;
  deleting: string | null;
  error: string | null;
  filterType: 'all' | 'company' | 'individual';
}

const initialState: TargetsState = {
  items: [],
  loading: false,
  deleting: null,
  error: null,
  filterType: 'all',
};

export const fetchTargets = createAsyncThunk(
  'targets/fetchTargets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/v1/settings/targets');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch targets');
    }
  }
);

export const createTarget = createAsyncThunk(
  'targets/createTarget',
  async (data: any, { rejectWithValue }) => {
    try {
      const response = await api.post('/v1/settings/targets', data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create target');
    }
  }
);

export const updateTarget = createAsyncThunk(
  'targets/updateTarget',
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/v1/settings/targets/${id}`, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update target');
    }
  }
);

export const deleteTarget = createAsyncThunk(
  'targets/deleteTarget',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(`/v1/settings/targets/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete target');
    }
  }
);

export const distributeTargets = createAsyncThunk(
  'targets/distributeTargets',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(`/v1/settings/targets/${id}/distribute`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to distribute targets');
    }
  }
);

const targetsSlice = createSlice({
  name: 'targets',
  initialState,
  reducers: {
    setFilterType: (state, action) => {
      state.filterType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTargets.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTargets.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.items = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
      })
      .addCase(fetchTargets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createTarget.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateTarget.fulfilled, (state, action) => {
        const index = state.items.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(deleteTarget.pending, (state, action) => {
        state.deleting = action.meta.arg;
      })
      .addCase(deleteTarget.fulfilled, (state, action) => {
        state.items = state.items.filter((t) => t.id !== action.payload);
        state.deleting = null;
      })
      .addCase(deleteTarget.rejected, (state, action) => {
        state.deleting = null;
        state.error = action.payload as string;
      })
      .addCase(distributeTargets.fulfilled, (state, action) => {
        const index = state.items.findIndex((t) => t.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      });
  },
});

export const { setFilterType } = targetsSlice.actions;

const selectTargetsState = (state: any) => state.targets;
const selectFilterType = (state: any) => state.targets.filterType;

export const selectTargets = createSelector(
  [selectTargetsState],
  (state) => state.items
);

export const selectFilteredTargets = createSelector(
  [selectTargets, selectFilterType],
  (items, filterType) => {
    if (filterType === 'all') return items;
    return items.filter((t) => t.type === filterType);
  }
);

export const selectTargetsLoading = createSelector(
  [selectTargetsState],
  (state) => state.loading
);

export const selectTargetsError = createSelector(
  [selectTargetsState],
  (state) => state.error
);

export const selectTargetsDeleting = createSelector(
  [selectTargetsState],
  (state) => state.deleting
);

export default targetsSlice.reducer;
